import config from '@/config';
import { logs, stringifyQuery } from '@shoppykey/tools';
import { AxiosError, request as fetch, history } from '@umijs/max';
import { notification } from 'antd';
import _ from 'lodash';
import { CustomException } from './custom-exception';
import { CustomExceptionTypes } from './custom-exception-types';
import { getCompanyId, getSysRoleId, getToken, getUtmChannel, isLogin } from './token';
import { getPageQuery } from './utils';

const codeMessage: Record<number, string> = {
  200: '服务器成功返回请求的数据。',
  201: '新建或修改数据成功。',
  202: '一个请求已经进入后台排队（异步任务）。',
  204: '删除数据成功。',
  400: '用户输入信息有误',
  401: '用户没有权限（令牌、用户名、密码错误）。',
  403: '访问受限',
  404: '记录不存在',
  405: '请求方法不被允许。',
  406: '请求的格式不可得。',
  410: '请求的资源被永久删除，且不会再得到的。',
  422: '当创建一个对象时，发生一个验证错误。',
  500: '服务器发生错误，请检查服务器。',
  502: '网关错误。',
  503: '服务不可用，服务器暂时过载或维护。',
  504: '网关超时。',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const defaultHeader: any = {};

type ResponseType = 'json' | 'text' | 'blob';

/**
 * 请求参数
 */
type FetchOptions = {
  getResponse?: boolean;
  isPublic?: boolean;
  needAuth?: boolean;
  query?: any;
  responseType?: ResponseType;
  silent?: boolean;
  headers?: any;
  signal?: any;
};

/**
 * 请求参数
 */
type BodyFetchOptions = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
} & FetchOptions;

type Options = {
  method: 'POST' | 'GET' | 'DELETE' | 'PUT';
  url: string;
} & BodyFetchOptions;

async function request(params: Options) {
  const headers = { ...defaultHeader, ...params?.headers };
  if (!params.isPublic) {
    const isLoggedIn = isLogin();
    if (!isLoggedIn) {
      const { redirect } = getPageQuery();
      history.replace(
        `/auth/login?${stringifyQuery({
          redirect: redirect || window.location.href,
        })}`,
      );
      // eslint-disable-next-line no-console
      throw new CustomException(CustomExceptionTypes.NEED_LOGIN, '需要登陆');
    }
  }
  headers.Authorization = getToken();
  const companyId = getCompanyId();
  const sysRoleId = getSysRoleId();
  const utmChannel = getUtmChannel();
  if (companyId) {
    headers['company-id'] = companyId;
  }
  if (config.APP_ID) {
    headers['app-id'] = config.APP_ID;
  }
  if (sysRoleId) {
    headers['company-role-id'] = sysRoleId;
  }
  if (utmChannel) {
    headers['utm_channel'] = utmChannel;
  }
  const options = {
    ...params,
    url: `${params.url}?${stringifyQuery(params.query)}`,
    headers,
  };

  try {
    const res = await fetch(options.url, { ...options, getResponse: true });
    if (_.get(res.headers, 'Deprecated')) {
      logs.warn(`${options.url} 接口已经废弃，请及时修改`);
    }
    if (res.data && res.data.errorCode) {
      throw new CustomException(res.data.errorCode, res.data.errorMessage);
    }
    if (options.getResponse) {
      return res;
    }
    return res.data.data;
  } catch (_error) {
    logs.error('出错了', _error);
    if (_.get(_error, 'name') === 'AxiosError') {
      const error = _error as AxiosError<any>;
      const status = error.response?.status;
      if (status === 401) {
        const { redirect } = getPageQuery();
        history.replace(
          `/auth/login?${stringifyQuery({
            redirect: redirect || window.location.href,
          })}`,
        );
        throw new CustomException(CustomExceptionTypes.NEED_LOGIN, '需要登陆');
      }
      if (status === 422) {
        throw new CustomException(CustomExceptionTypes.UNPROCESSABLE_ENTITY, '重复请求');
      }
      const messages = error.response?.data?.message || error.response?.data?.messages;
      if (!options.silent && status) {
        const message = codeMessage[status] || '';
        notification.error({
          message,
          description: messages,
        });
      }
      throw new CustomException(
        CustomExceptionTypes.UNKNOWN_ERROR,
        _.get(error, 'response.data.message', error.message),
      );
    }
    throw new CustomException(CustomExceptionTypes.UNKNOWN_ERROR, '未知错误');
  }
}

export class FetchClient {
  public static async post<T>(url: string, option?: BodyFetchOptions): Promise<T> {
    return request({ method: 'POST', url, ...option });
  }

  public static async delete<T>(url: string, option?: BodyFetchOptions): Promise<T> {
    return request({ method: 'DELETE', url, ...option });
  }

  public static async put<T>(url: string, option?: BodyFetchOptions): Promise<T> {
    return request({ method: 'PUT', url, ...option });
  }

  public static async get<T>(url: string, option?: FetchOptions): Promise<T> {
    return request({ method: 'GET', url, ...option });
  }
}
